import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth, db, functions } from './firebase';
import {
  collection, query, orderBy, limit, onSnapshot, updateDoc, doc
} from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { acceptConnect, removeConnect } from './connections';

function timeAgo(date) {
  const seconds = Math.floor((Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

function NotificationItem({ notif, onOpen, onAccept, onDecline }) {
  const createdAt = notif.createdAt?.toDate?.() ?? new Date();
  const isNew = !notif.read;
  const isConnectionRequest = notif.type === 'connection_request' || notif.type === 'connect_request';
  const actioned = notif.actioned;

  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'flex-start',
        gap: 12,
        padding: '14px 0',
        borderBottom: '1px solid var(--border)',
        opacity: isNew ? 1 : 0.65,
        cursor: notif.fromUsername ? 'pointer' : 'default',
      }}
      onClick={() => onOpen(notif)}
    >
      <div
        style={{
          width: 40, height: 40, borderRadius: '50%',
          background: 'var(--card-2)', flexShrink: 0, overflow: 'hidden',
          border: isNew ? '2px solid var(--accent)' : '2px solid var(--border)',
        }}
      >
        {notif.fromPhotoURL
          ? <img src={notif.fromPhotoURL} alt="" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
          : <div style={{ width: '100%', height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 700, fontSize: 16, color: 'var(--accent)' }}>
              {notif.fromDisplayName?.[0]?.toUpperCase() || '?'}
            </div>
        }
      </div>

      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontSize: 14, lineHeight: 1.5 }}>{notif.body}</div>
        <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 3 }}>{timeAgo(createdAt)}</div>

        {isConnectionRequest && !actioned && (
          <div style={{ display: 'flex', gap: 8, marginTop: 10 }}>
            <button
              className="btn btn-primary btn-sm"
              onClick={(e) => { e.stopPropagation(); onAccept(notif); }}
            >
              Accept
            </button>
            <button
              className="btn btn-sm"
              style={{ background: 'var(--card-2)', border: '1px solid var(--border)' }}
              onClick={(e) => { e.stopPropagation(); onDecline(notif); }}
            >
              Decline
            </button>
          </div>
        )}
        {isConnectionRequest && actioned === 'accepted' && (
          <div style={{ fontSize: 12, color: 'var(--accent)', marginTop: 6 }}>Accepted</div>
        )}
        {isConnectionRequest && actioned === 'declined' && (
          <div style={{ fontSize: 12, color: 'var(--muted)', marginTop: 6 }}>Declined</div>
        )}
      </div>

      {isNew && !isConnectionRequest && (
        <div style={{ width: 8, height: 8, borderRadius: '50%', background: 'var(--accent)', flexShrink: 0, marginTop: 6 }} />
      )}
    </div>
  );
}

function Notifications({ onClose, currentUserProfile }) {
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const uid = auth.currentUser?.uid;
  const navigate = useNavigate();

  useEffect(() => {
    if (!uid) return;
    const q = query(
      collection(db, 'users', uid, 'notifications'),
      orderBy('createdAt', 'desc'),
      limit(30)
    );
    const unsub = onSnapshot(q, (snap) => {
      setNotifications(snap.docs.map(d => ({ id: d.id, ...d.data() })));
      setLoading(false);
    });
    return unsub;
  }, [uid]);

  const handleRead = async (notifId) => {
    if (!uid) return;
    await updateDoc(doc(db, 'users', uid, 'notifications', notifId), { read: true });
  };

  // Tapping a notification opens the person it's about, and marks it read.
  const handleOpen = (notif) => {
    if (!notif.read) handleRead(notif.id);
    // System notices (e.g. a removed photo) route to the appeal path, not a profile.
    if (notif.type === 'photo_removed') {
      onClose?.();
      navigate('/feedback');
      return;
    }
    if (notif.fromUsername && notif.fromUid !== 'system') {
      onClose?.();
      navigate(`/${notif.fromUsername}`);
    }
  };

  const me = () => ({
    username: currentUserProfile?.username,
    displayName: currentUserProfile?.displayName,
    photoURL: currentUserProfile?.photoURL,
  });

  const handleAccept = async (notif) => {
    if (!uid) return;
    try {
      if (notif.type === 'connect_request') {
        // Profile-level connection: recipient accepts, updates the connection doc.
        await acceptConnect(uid, notif.fromUid, me());
      } else {
        // Tile-level: the cross-user update + confirmation happen server-side.
        const respond = httpsCallable(functions, 'respondToConnection');
        await respond({ fromUid: notif.fromUid, tileId: notif.tileId, accept: true });
      }
      await updateDoc(doc(db, 'users', uid, 'notifications', notif.id), { read: true, actioned: 'accepted' });
    } catch (e) {
      console.error('accept failed', e);
    }
  };

  const handleDecline = async (notif) => {
    if (!uid) return;
    try {
      if (notif.type === 'connect_request') {
        await removeConnect(uid, notif.fromUid);
      } else {
        const respond = httpsCallable(functions, 'respondToConnection');
        await respond({ fromUid: notif.fromUid, tileId: notif.tileId, accept: false });
      }
      await updateDoc(doc(db, 'users', uid, 'notifications', notif.id), { read: true, actioned: 'declined' });
    } catch (e) {
      console.error('decline failed', e);
    }
  };

  const handleMarkAllRead = async () => {
    if (!uid) return;
    const unread = notifications.filter(n => !n.read);
    await Promise.all(
      unread.map(n =>
        updateDoc(doc(db, 'users', uid, 'notifications', n.id), { read: true })
      )
    );
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="notif-overlay" onClick={onClose}>
      <div
        style={{
          background: 'var(--surface)',
          border: '1px solid var(--border)',
          borderRadius: 'var(--radius-lg)',
          width: '100%',
          maxWidth: 380,
          boxShadow: 'var(--shadow)',
          pointerEvents: 'auto',
          // Natural height: header + the full list, no internal scroll. The
          // overlay scrolls if the panel is taller than the viewport. Any scroll
          // container here (overflow/max-height/flex:1/sticky) collapses to zero
          // in the Android WebView's shrink-to-fit box. This is the same pattern
          // that fixed the modals.
          animation: 'slideUp 0.2s ease',
        }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid var(--border)',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <strong style={{ fontSize: 16 }}>Notifications</strong>
            {unreadCount > 0 && (
              <span
                style={{
                  background: 'var(--primary)',
                  color: '#0d0d0d',
                  fontSize: 11,
                  fontWeight: 700,
                  borderRadius: 999,
                  padding: '2px 7px',
                }}
              >
                {unreadCount}
              </span>
            )}
          </div>
          <div style={{ display: 'flex', gap: 8 }}>
            {unreadCount > 0 && (
              <button
                className="btn btn-ghost btn-sm"
                onClick={handleMarkAllRead}
              >
                Mark all read
              </button>
            )}
            <button
              onClick={onClose}
              style={{
                background: 'none',
                border: 'none',
                color: 'var(--text-muted)',
                fontSize: 18,
                cursor: 'pointer',
                padding: 4,
              }}
            >
              ✕
            </button>
          </div>
        </div>

        {/* List */}
        <div
          style={{
            padding: '0 20px',
          }}
        >
          {loading ? (
            <div style={{ display: 'flex', justifyContent: 'center', padding: '32px 0' }}>
              <span className="spinner" />
            </div>
          ) : notifications.length === 0 ? (
            <div
              style={{
                textAlign: 'center',
                padding: '40px 0',
                color: 'var(--text-muted)',
              }}
            >
              <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ marginBottom: 12, opacity: 0.3 }}>
                <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
                <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
              </svg>
              <div style={{ fontSize: 14 }}>No notifications yet</div>
              <div style={{ fontSize: 13, marginTop: 6 }}>
                You'll hear when someone changes your rank.
              </div>
            </div>
          ) : (
            notifications.map(notif => (
              <NotificationItem
                key={notif.id}
                notif={notif}
                onOpen={handleOpen}
                onAccept={handleAccept}
                onDecline={handleDecline}
              />
            ))
          )}
        </div>
      </div>
    </div>
  );
}

export default Notifications;
export { Notifications };
